// Maps gerund forms of common irregular verbs to their past tense.
const IRREGULAR_GERUNDS: Record<string, string> = {
	beginning: 'began',
	bringing: 'brought',
	building: 'built',
	buying: 'bought',
	coming: 'came',
	doing: 'did',
	finding: 'found',
	getting: 'got',
	giving: 'gave',
	going: 'went',
	having: 'had',
	keeping: 'kept',
	knowing: 'knew',
	letting: 'let',
	making: 'made',
	meeting: 'met',
	putting: 'put',
	reading: 'read',
	running: 'ran',
	seeing: 'saw',
	sending: 'sent',
	setting: 'set',
	sitting: 'sat',
	taking: 'took',
	telling: 'told',
	thinking: 'thought',
	understanding: 'understood',
	writing: 'wrote',
};

/**
 * Converts the first word of a label from present-continuous (gerund) to past tense.
 * Returns the label unchanged if the first word is not a recognisable gerund.
 *
 * e.g. "Searching product database" → "Searched product database"
 *      "Analyzing results"          → "Analyzed results"
 *      "The user is asking…"        → "The user is asking…"  (unchanged)
 */
export function toPastTense(label: string): string {
	const trimmed = label.trim();
	const spaceIdx = trimmed.search(/\s/);
	const firstWord = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
	const rest = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx);

	const lower = firstWord.toLowerCase();
	if (!lower.endsWith('ing')) return label;

	const irregular = IRREGULAR_GERUNDS[lower];
	let pastVerb: string;

	if (irregular) {
		pastVerb = irregular;
	} else {
		// Strip 'ing'; stripping gives either the base (search→search) or a
		// base with a dropped silent-e restored implicitly by adding 'ed'
		// (generat→generated, analyz→analyzed, creat→created).
		pastVerb = lower.slice(0, -3) + 'ed';
	}

	// Preserve original capitalisation of the first letter.
	if (firstWord[0] === firstWord[0].toUpperCase()) {
		pastVerb = pastVerb.charAt(0).toUpperCase() + pastVerb.slice(1);
	}

	return pastVerb + rest;
}

/**
 * Derives a short past-tense summary sentence from a list of step labels.
 * Returns null when a reliable summary cannot be produced (e.g. labels don't
 * start with gerunds), so callers can fall back to a count string.
 *
 * e.g. ["Searching trends", "Analysing posts", "Fetching data"]
 *      → "Searched trends, analysed posts, and fetched data"
 */
export function buildGroupSummary(labels: string[]): string | null {
	if (labels.length === 0) return null;

	const transformed = labels.map(toPastTense);

	// If any label was unchanged and its first word isn't a gerund, bail out.
	const allGerunds = labels.every((orig, i) => {
		if (transformed[i] !== orig) return true; // successfully transformed
		const first = orig.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
		return first.endsWith('ing'); // same result is fine if it was already past-tense-like
	});
	if (!allGerunds) return null;

	// Cap at 3 clauses for readability; the label truncates at the display layer anyway.
	const clauses = transformed
		.slice(0, 3)
		.map((t, i) => (i === 0 ? t : t.charAt(0).toLowerCase() + t.slice(1)));

	if (clauses.length === 1) return clauses[0];
	if (clauses.length === 2) return `${clauses[0]} and ${clauses[1]}`;
	return `${clauses[0]}, ${clauses[1]}, and ${clauses[2]}`;
}

/**
 * Formats an elapsed duration the way the chat "thinking time" prefix reads:
 * compact h/m/s with the two most-significant non-zero units.
 *
 * e.g. 7s → "7s", 53s → "53s", 66s → "1m 6s", 75s → "1m 15s",
 *      3720s → "1h 2m", 0s → "0s"
 *
 * Sub-second input rounds down to whole seconds (a fresh turn shows "0s", not
 * a flickering fraction). Negative input is clamped to 0.
 */
export function formatThinkingDuration(totalSeconds: number): string {
	const s = Math.max(0, Math.floor(totalSeconds));
	const hours = Math.floor(s / 3600);
	const minutes = Math.floor((s % 3600) / 60);
	const seconds = s % 60;

	if (hours > 0) return `${hours}h ${minutes}m`;
	if (minutes > 0) return `${minutes}m ${seconds}s`;
	return `${seconds}s`;
}
